'use client'
import Image from 'next/image'
import dynamic from 'next/dynamic'

import { cn } from '@/utils/twMerge'
import type { Settings } from 'react-slick'
import '@/styles/vendors/slickCarousel'
import { PrismicRichText } from '@prismicio/react'
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next'
import { reviewSliderSettingsWithoutDot, reviewSliderSettingsWithDot } from '@/utils/sliderSettings'
import { getClientDesignation, getClientIcon, getClientName, getClientQuote } from '@/utils/getReviewData'

// Lazy load Slider to reduce initial bundle size
const Slider = dynamic<Settings>(() => import('react-slick'), {
  ssr: false,
  loading: () => <div className="h-64 animate-pulse rounded-md bg-gray-100" />,
})

type DataObject = Record<string, any>
type ItemObject = Record<string, any>

const ReviewComponent = ({ data, type }: { data: DataObject; type?: string }) => {
  const showBg: string[] = ['whyReview']
  const showSliderSettings = ['whyReview', 'review'].includes(type as string)
    ? reviewSliderSettingsWithoutDot
    : reviewSliderSettingsWithDot

  return (
    <section
      className={cn('relative z-[1] pb-20 lg:pb-[120px]', type === 'whyReview' ? 'pb-0 pt-20 lg:pt-[120px]' : '')}>
      {/* Background Image - fixed dimensions to prevent CLS */}
      {showBg.includes(type as string) && (
        <div className="absolute right-0 -z-[1] xs:-top-[8%] sm:-top-[2%] lg:-top-[4%] xl:-top-[6%]">
          <Image
            src="/images/customers-bg.svg"
            alt=""
            aria-hidden="true"
            width={1920}
            height={800}
            sizes="100vw"
            className="!h-[750px] w-auto sm:!h-[550px] md:!h-[600px] lg:!h-auto lg:!w-full"
            loading="lazy"
          />
        </div>
      )}

      <div className="container">
        {/* Header Section */}
        <div className="mb-11 text-center lg:mx-auto lg:max-w-5xl">
          <PrismicRichText field={data.primary.section_title} />
          <PrismicRichText
            field={data.primary.section_description}
            components={{
              paragraph: ({ children }) => <p className="mb-14 mt-8">{children}</p>,
            }}
          />
        </div>

        {/* Review Cards Slider */}
        <div className="[&_.slick-slider]:-mx-4 [&_.slick-slider]:my-0 [&_.slick-slider_.slick-active_button::before]:text-primary-green-85 [&_.slick-slider_.slick-dots]:-bottom-11 [&_.slick-slider_.slick-dots_li]:m-0 [&_.slick-slider_.slick-slide]:px-3 [&_.slick-slider_button::before]:text-xs [&_.slick-slider_button::before]:text-primary-ink-30 [&_.slick-slider_button::before]:opacity-100">
          <Slider {...showSliderSettings}>
            {data.items.map((item: ItemObject, index: number) => {
              const key = `i-${index}`
              return (
                <div
                  key={key}
                  className="!grid overflow-hidden rounded-md shadow-dark backdrop-blur-25 lg:!grid-cols-6">
                  {/* Left Content */}
                  <div className="row-start-2 flex h-full flex-col bg-primary-ink-100 p-5 lg:col-span-4 lg:row-start-1 lg:px-14 lg:py-11">
                    <PrismicNextImage field={item.company_icon} className="max-w-[150px]" fallbackAlt="" />

                    <div className="flex h-full flex-col justify-between">
                      <PrismicRichText
                        field={getClientQuote(type as string, item)}
                        components={{
                          paragraph: ({ children }) => (
                            <p
                              className={cn(
                                'm-0 mb-6 mt-8 font-halyardDisplay text-white',
                                type === 'marketingReview' ? 'text-[30px] leading-[110%] -tracking-[1.5%]' : 'text-xl',
                              )}>
                              {children}
                            </p>
                          ),
                        }}
                      />

                      <div>
                        <PrismicRichText
                          field={getClientName(type as string, item)}
                          components={{
                            heading5: ({ children }) => (
                              <h5 className="m-0 font-halyardText font-medium text-white">{children}</h5>
                            ),
                          }}
                        />
                        <PrismicRichText
                          field={getClientDesignation(type as string, item)}
                          components={{
                            paragraph: ({ children }) => (
                              <p className="m-0 font-halyardText font-normal text-white/80">{children}</p>
                            ),
                          }}
                        />
                      </div>

                      {['partner-details', 'whyReview', 'marketingReview'].includes(type as string) ? undefined : (
                        <PrismicNextLink
                          field={item.read_case_study_url}
                          className="btn btn-text btn-small btn-interaction mt-8 !w-fit !p-0">
                          <span className="text-white">{item.read_case_study}</span>
                          <Image src="/images/angle-right-green.svg" width={20} height={16} alt="" aria-hidden="true" />
                        </PrismicNextLink>
                      )}
                    </div>
                  </div>

                  {/* Right Image */}
                  <div
                    className="row-start-1 h-[350px] bg-cover bg-center bg-no-repeat lg:col-span-2 lg:col-start-5 lg:h-[400px]"
                    style={{ backgroundImage: `url(${getClientIcon(type as string, item)})` }}
                  />
                </div>
              )
            })}
          </Slider>
        </div>
      </div>
    </section>
  )
}

export default ReviewComponent
